import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Form, Button, Badge, Modal, Tab, Tabs, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Area, AreaChart } from 'recharts';
import { fetchAuditLogs, fetchLogStats, exportLogs, fetchActivityByHour } from '../../services/api';
import DatePicker from '../../components/DatePicker/DatePicker';
import {
    fetchLoginAttempts,
    fetchAlerts,
    markAlertAsRead,
    fetchPermissionChanges,
    fetchActiveSessions,
    fetchConcurrentSessions,
    fetchUsersLastAccess,
    fetchDeviceStats,
    fetchSlowEndpoints,
    fetchErrorsByType,
    fetchServerMetrics,
    fetchAnalyticsSummary,
    fetchTopProducts,
    fetchTopFeatures,
    fetchPeakHours,
    fetchUsageTrend,
    fetchUsageComparison,
    fetchUserTimeline
} from '../../services/logsApi';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import HealthCheckTab from '../../components/HealthCheckTab';
import React, { Suspense, lazy } from 'react';

// Lazy load para o JobsSchedulerTab
const JobsSchedulerTab = lazy(() => import('../../components/JobsSchedulerTab'));

// Cores para gráficos
const COLORS = ['#0d6efd', '#198754', '#ffc107', '#dc3545', '#6f42c1', '#0dcaf0', '#fd7e14', '#20c997'];

const LogsViewer = () => {
    const { isAdmin } = useAuth();
    const navigate = useNavigate();

    // Estados gerais
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Estados para Overview
    const [stats, setStats] = useState(null);
    const [activityByHour, setActivityByHour] = useState([]);
    const [analyticsSummary, setAnalyticsSummary] = useState(null);
    const [usageComparison, setUsageComparison] = useState(null);

    // Estados para Segurança
    const [loginAttempts, setLoginAttempts] = useState({ attempts: [], stats: [] });
    const [alerts, setAlerts] = useState([]);
    const [permissionChanges, setPermissionChanges] = useState([]);

    // Estados para Sessões
    const [activeSessions, setActiveSessions] = useState({ sessions: [], totalActive: 0 });
    const [concurrentSessions, setConcurrentSessions] = useState([]);
    const [usersLastAccess, setUsersLastAccess] = useState([]);
    const [deviceStats, setDeviceStats] = useState([]);

    // Estados para Performance
    const [slowEndpoints, setSlowEndpoints] = useState([]);
    const [errorsByType, setErrorsByType] = useState([]);
    const [serverMetrics, setServerMetrics] = useState(null);

    // Estados para Analytics
    const [topProducts, setTopProducts] = useState([]);
    const [topFeatures, setTopFeatures] = useState([]);
    const [peakHours, setPeakHours] = useState([]);
    const [usageTrend, setUsageTrend] = useState([]);

    const [selectedLog, setSelectedLog] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [filters, setFilters] = useState({
        action: '',
        status: '',
        userId: '',
        search: '',
        startDate: '',
        endDate: '',
        limit: 50,
        offset: 0
    });
    const [pagination, setPagination] = useState({ total: 0, hasMore: false });

    // Timeline Modal
    const [showTimelineModal, setShowTimelineModal] = useState(false);
    const [timelineUser, setTimelineUser] = useState(null);
    const [timelineData, setTimelineData] = useState(null);

    // Backup Google Drive
    const [backupLoading, setBackupLoading] = useState(false);
    const [backupResult, setBackupResult] = useState(null);

    useEffect(() => {
        if (!isAdmin()) {
            navigate('/');
            return;
        }
        loadData();
    }, [activeTab]);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        try {
            switch (activeTab) {
                case 'overview':
                    await loadOverviewData();
                    break;
                case 'security':
                    await loadSecurityData();
                    break;
                case 'sessions':
                    await loadSessionsData();
                    break;
                case 'performance':
                    await loadPerformanceData();
                    break;
                case 'analytics':
                    await loadAnalyticsData();
                    break;
                case 'audit':
                    await loadAuditData();
                    break;
            }
        } catch (error) {
            console.error('Erro ao carregar dados:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const loadOverviewData = async () => {
        const [statsRes, activityRes, summaryRes, comparisonRes] = await Promise.all([
            fetchLogStats({}),
            fetchActivityByHour(),
            fetchAnalyticsSummary(),
            fetchUsageComparison()
        ]);

        if (statsRes.success) setStats(statsRes.data);
        if (activityRes.success) setActivityByHour(activityRes.data);
        if (summaryRes.success) setAnalyticsSummary(summaryRes.data);
        if (comparisonRes.success) setUsageComparison(comparisonRes.data);
    };

    const loadSecurityData = async () => {
        const [attemptsRes, alertsRes, permRes] = await Promise.all([
            fetchLoginAttempts(50, false),
            fetchAlerts(50),
            fetchPermissionChanges(50)
        ]);

        if (attemptsRes.success) setLoginAttempts(attemptsRes.data);
        if (alertsRes.success) setAlerts(alertsRes.data);
        if (permRes.success) setPermissionChanges(permRes.data);
    };

    const loadSessionsData = async () => {
        const [sessionsRes, concurrentRes, lastAccessRes, devicesRes] = await Promise.all([
            fetchActiveSessions(),
            fetchConcurrentSessions(),
            fetchUsersLastAccess(),
            fetchDeviceStats(30)
        ]);

        if (sessionsRes.success) setActiveSessions(sessionsRes.data);
        if (concurrentRes.success) setConcurrentSessions(concurrentRes.data);
        if (lastAccessRes.success) setUsersLastAccess(lastAccessRes.data);
        if (devicesRes.success) setDeviceStats(devicesRes.data);
    };

    const loadPerformanceData = async () => {
        const [slowRes, errorsRes, metricsRes] = await Promise.all([
            fetchSlowEndpoints(1000, 7, 20),
            fetchErrorsByType(7, 20),
            fetchServerMetrics(24)
        ]);

        if (slowRes.success) setSlowEndpoints(slowRes.data);
        if (errorsRes.success) setErrorsByType(errorsRes.data);
        if (metricsRes.success) setServerMetrics(metricsRes.data);
    };

    const loadAnalyticsData = async () => {
        const [productsRes, featuresRes, hoursRes, trendRes] = await Promise.all([
            fetchTopProducts(30, 10),
            fetchTopFeatures(30, 10),
            fetchPeakHours(7),
            fetchUsageTrend(14)
        ]);

        if (productsRes.success) setTopProducts(productsRes.data);
        if (featuresRes.success) setTopFeatures(featuresRes.data);
        if (hoursRes.success) setPeakHours(hoursRes.data);
        if (trendRes.success) setUsageTrend(trendRes.data);
    };

    const loadAuditData = async () => {
        const logsRes = await fetchAuditLogs(filters);
        if (logsRes.success) {
            setLogs(logsRes.data.logs);
            setPagination({ total: logsRes.data.total, hasMore: logsRes.data.hasMore });
        }
    };


    const handleExport = async (format) => {
        try {
            await exportLogs(filters, format);
            alert(`Logs exportados em ${format.toUpperCase()}`);
        } catch (error) {
            alert('Erro ao exportar logs');
        }
    };

    const handleMarkAlertRead = async (alertId) => {
        try {
            await markAlertAsRead(alertId);
            setAlerts(alerts.filter(a => a.id !== alertId));
        } catch (error) {
            console.error('Erro ao marcar alerta:', error);
        }
    };

    const handleViewTimeline = async (user) => {
        setTimelineUser(user);
        setShowTimelineModal(true);
        try {
            const res = await fetchUserTimeline(user.id || user.usuario_id, 7);
            if (res.success) {
                setTimelineData(res.data);
            }
        } catch (error) {
            console.error('Erro ao carregar timeline:', error);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('pt-BR');
    };

    const formatDuration = (ms) => {
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(2)}s`;
    };

    const getStatusBadge = (status) => {
        return status === 'success'
            ? <Badge bg="success">Sucesso</Badge>
            : <Badge bg="danger">Erro</Badge>;
    };

    const getActionBadge = (action) => {
        const colors = {
            LOGIN: 'primary',
            LOGOUT: 'secondary',
            CREATE: 'success',
            UPDATE: 'warning',
            DELETE: 'danger',
            READ: 'info',
            REGISTER: 'info'
        };
        return <Badge bg={colors[action] || 'secondary'}>{action}</Badge>;
    };

    const getSeverityBadge = (severity) => {
        const colors = { info: 'info', warning: 'warning', critical: 'danger' };
        return <Badge bg={colors[severity] || 'secondary'}>{severity}</Badge>;
    };

    if (!isAdmin()) return null;

    const handleGoogleDriveBackup = async () => {
        setBackupLoading(true);
        setBackupResult(null);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/admin/backup/google-drive', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            setBackupResult(data);
        } catch (err) {
            setBackupResult({ success: false, message: err.message });
        } finally {
            setBackupLoading(false);
        }
    };

    // Backup Limeira
    const [limeiraLoading, setLimeiraLoading] = useState(false);
    const [limeiraResult, setLimeiraResult] = useState(null);

    const handleLimeiraBackup = async () => {
        setLimeiraLoading(true);
        setLimeiraResult(null);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/admin/backup/limeira', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            setLimeiraResult(data);
        } catch (err) {
            setLimeiraResult({ success: false, message: err.message });
        } finally {
            setLimeiraLoading(false);
        }
    };

    // Backup Usuários (Tags, Notificações, Usuários)
    const [usuariosLoading, setUsuariosLoading] = useState(false);
    const [usuariosResult, setUsuariosResult] = useState(null);

    const handleUsuariosBackup = async () => {
        setUsuariosLoading(true);
        setUsuariosResult(null);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/admin/backup/usuarios', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            setUsuariosResult(data);
        } catch (err) {
            setUsuariosResult({ success: false, message: err.message });
        } finally {
            setUsuariosLoading(false);
        }
    };

    return (
        <Container fluid className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mb-0">📋 Sistema de Logs Avançado</h2>
                <div className="d-flex gap-2 flex-wrap">
                    <Button
                        variant="outline-warning"
                        onClick={handleUsuariosBackup}
                        disabled={usuariosLoading}
                        size="sm"
                    >
                        {usuariosLoading ? (
                            <><Spinner animation="border" size="sm" className="me-2" /> Enviando...</>
                        ) : (
                            '🏷️ Backup Tags/Usuários'
                        )}
                    </Button>
                    <Button
                        variant="outline-success"
                        onClick={handleLimeiraBackup}
                        disabled={limeiraLoading}
                        size="sm"
                    >
                        {limeiraLoading ? (
                            <><Spinner animation="border" size="sm" className="me-2" /> Enviando...</>
                        ) : (
                            '📦 Backup Limeira'
                        )}
                    </Button>
                    <Button
                        variant="outline-primary"
                        onClick={handleGoogleDriveBackup}
                        disabled={backupLoading}
                        size="sm"
                    >
                        {backupLoading ? (
                            <><Spinner animation="border" size="sm" className="me-2" /> Enviando...</>
                        ) : (
                            '☁️ Backup Completo'
                        )}
                    </Button>
                </div>
            </div>

            {backupResult && (
                <Alert
                    variant={backupResult.success ? 'success' : 'danger'}
                    dismissible
                    onClose={() => setBackupResult(null)}
                >
                    <strong>{backupResult.success ? '✅' : '❌'}</strong> {backupResult.message}
                    {backupResult.files && backupResult.files.length > 0 && (
                        <ul className="mb-0 mt-2">
                            {backupResult.files.map((f, i) => (
                                <li key={i}>{f.file}: {f.success ? '✓' : '✗'}</li>
                            ))}
                        </ul>
                    )}
                </Alert>
            )}

            {limeiraResult && (
                <Alert
                    variant={limeiraResult.success ? 'success' : 'danger'}
                    dismissible
                    onClose={() => setLimeiraResult(null)}
                >
                    <strong>{limeiraResult.success ? '📦' : '❌'}</strong> {limeiraResult.message}
                    {limeiraResult.stats && (
                        <div className="mt-2">
                            <small>
                                <strong>Estatísticas:</strong> {limeiraResult.stats.solicitacoes || 0} solicitações, {limeiraResult.stats.pedidos || 0} pedidos
                            </small>
                        </div>
                    )}
                    {limeiraResult.files && limeiraResult.files.length > 0 && (
                        <ul className="mb-0 mt-2">
                            {limeiraResult.files.map((f, i) => (
                                <li key={i}>{f.file}: {f.success ? '✓' : '✗'}</li>
                            ))}
                        </ul>
                    )}
                </Alert>
            )}

            {usuariosResult && (
                <Alert
                    variant={usuariosResult.success ? 'success' : 'danger'}
                    dismissible
                    onClose={() => setUsuariosResult(null)}
                >
                    <strong>{usuariosResult.success ? '🏷️' : '❌'}</strong> {usuariosResult.message}
                    {usuariosResult.stats && (
                        <div className="mt-2">
                            <small>
                                <strong>Estatísticas:</strong> {usuariosResult.stats.tags || 0} tags, {usuariosResult.stats.notificacoes || 0} notificações, {usuariosResult.stats.usuarios || 0} usuários
                                {usuariosResult.stats.tarefas !== undefined && `, ${usuariosResult.stats.tarefas} tarefas`}
                            </small>
                        </div>
                    )}
                    {usuariosResult.files && usuariosResult.files.length > 0 && (
                        <ul className="mb-0 mt-2">
                            {usuariosResult.files.map((f, i) => (
                                <li key={i}>{f.file}: {f.success ? '✓' : '✗'}</li>
                            ))}
                        </ul>
                    )}
                </Alert>
            )}


            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    <strong>❌ Erro:</strong> {error}
                </Alert>
            )}

            <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-4">
                <Tab eventKey="overview" title="📊 Visão Geral">
                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" /></div>
                    ) : (
                        <OverviewTab
                            stats={stats}
                            activityByHour={activityByHour}
                            analyticsSummary={analyticsSummary}
                            usageComparison={usageComparison}
                        />
                    )}
                </Tab>

                <Tab eventKey="security" title="🔐 Segurança">
                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" /></div>
                    ) : (
                        <SecurityTab
                            loginAttempts={loginAttempts}
                            alerts={alerts}
                            permissionChanges={permissionChanges}
                            onMarkAlertRead={handleMarkAlertRead}
                            formatDate={formatDate}
                            getSeverityBadge={getSeverityBadge}
                        />
                    )}
                </Tab>

                <Tab eventKey="sessions" title="👥 Sessões">
                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" /></div>
                    ) : (
                        <SessionsTab
                            activeSessions={activeSessions}
                            concurrentSessions={concurrentSessions}
                            usersLastAccess={usersLastAccess}
                            deviceStats={deviceStats}
                            formatDate={formatDate}
                            onViewTimeline={handleViewTimeline}
                        />
                    )}
                </Tab>

                <Tab eventKey="performance" title="⚡ Performance">
                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" /></div>
                    ) : (
                        <PerformanceTab
                            slowEndpoints={slowEndpoints}
                            errorsByType={errorsByType}
                            serverMetrics={serverMetrics}
                            formatDuration={formatDuration}
                            formatDate={formatDate}
                        />
                    )}
                </Tab>

                <Tab eventKey="analytics" title="📈 Analytics">
                    {loading ? (
                        <div className="text-center py-5"><Spinner animation="border" /></div>
                    ) : (
                        <AnalyticsTab
                            topProducts={topProducts}
                            topFeatures={topFeatures}
                            peakHours={peakHours}
                            usageTrend={usageTrend}
                        />
                    )}
                </Tab>

                <Tab eventKey="healthcheck" title="🔍 Health Check">
                    <HealthCheckTab />
                </Tab>

                <Tab eventKey="jobs" title="⚙️ Jobs">
                    <Suspense fallback={<div className="text-center py-5"><Spinner animation="border" /><p>Carregando Jobs...</p></div>}>
                        <JobsSchedulerTab />
                    </Suspense>
                </Tab>
                
            </Tabs>

            {/* Modal de Detalhes do Log */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Detalhes da Ação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedLog && (
                        <div>
                            <h6>Informações do Usuário</h6>
                            <p><strong>Email:</strong> {selectedLog.user_email || 'N/A'}</p>
                            <p><strong>IP:</strong> {selectedLog.ip_address}</p>
                            <p><strong>User Agent:</strong> {selectedLog.user_agent}</p>

                            <hr />

                            <h6>Detalhes da Ação</h6>
                            <p><strong>Tipo:</strong> {selectedLog.action || selectedLog.acao}</p>
                            <p><strong>Método:</strong> {selectedLog.method}</p>
                            <p><strong>Endpoint:</strong> {selectedLog.endpoint}</p>
                            <p><strong>Status:</strong> {selectedLog.status}</p>
                            <p><strong>Duração:</strong> {selectedLog.duration_ms}ms</p>

                            {selectedLog.request_body && (
                                <>
                                    <hr />
                                    <h6>Request Body</h6>
                                    <pre className="bg-light p-2 rounded">{selectedLog.request_body}</pre>
                                </>
                            )}

                            {selectedLog.error_message && (
                                <>
                                    <hr />
                                    <h6 className="text-danger">Erro</h6>
                                    <p>{selectedLog.error_message}</p>
                                </>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>Fechar</Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Timeline do Usuário */}
            <Modal show={showTimelineModal} onHide={() => setShowTimelineModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Timeline de Atividades - {timelineUser?.email || timelineUser?.nome}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {timelineData ? (
                        <div>
                            <p className="text-muted mb-3">Total de {timelineData.totalActions} ações nos últimos 7 dias</p>
                            {timelineData.timeline && timelineData.timeline.length > 0 ? (
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {timelineData.timeline.map((item, index) => (
                                        <div key={index} className="d-flex border-bottom py-2">
                                            <div className="me-3">
                                                {getActionBadge(item.action)}
                                            </div>
                                            <div className="flex-grow-1">
                                                <div><strong>{item.resource}</strong> - {item.endpoint}</div>
                                                <small className="text-muted">{formatDate(item.timestamp)} • {item.duration_ms}ms</small>
                                            </div>
                                            <div>
                                                {getStatusBadge(item.status)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted">Nenhuma atividade encontrada</p>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                    )}
                </Modal.Body>
            </Modal>
        </Container>
    );
};

// ============================================
// TAB COMPONENTS
// ============================================


const OverviewTab = ({ stats, activityByHour, analyticsSummary, usageComparison }) => (
    <>
        {/* Cards de Estatísticas */}
        <Row className="mb-4">
            <Col md={3}>
                <Card className="text-center h-100" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                    <Card.Body>
                        <h6 className="opacity-75">Total de Ações</h6>
                        <h2>{stats?.totalActions?.toLocaleString() || 0}</h2>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={3}>
                <Card className="text-center h-100" style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white' }}>
                    <Card.Body>
                        <h6 className="opacity-75">Usuários Ativos (24h)</h6>
                        <h2>{stats?.activeUsers || 0}</h2>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={3}>
                <Card className="text-center h-100" style={{ background: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)', color: 'white' }}>
                    <Card.Body>
                        <h6 className="opacity-75">Erros (24h)</h6>
                        <h2>{stats?.recentErrors || 0}</h2>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={3}>
                <Card className="text-center h-100" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                    <Card.Body>
                        <h6 className="opacity-75">Ações Hoje</h6>
                        <h2>{analyticsSummary?.actionsToday || 0}</h2>
                        {usageComparison && (
                            <small className={usageComparison.vsYesterday >= 0 ? 'text-success' : 'text-danger'}>
                                {usageComparison.vsYesterday >= 0 ? '↑' : '↓'} {Math.abs(usageComparison.vsYesterday)}% vs ontem
                            </small>
                        )}
                    </Card.Body>
                </Card>
            </Col>
        </Row>

        {/* Gráfico de Atividade por Hora */}
        <Row className="mb-4">
            <Col md={8}>
                <Card>
                    <Card.Header>📊 Atividade por Hora (Últimas 24h)</Card.Header>
                    <Card.Body>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={activityByHour}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="count" name="Total de Ações" fill="#0d6efd" />
                                <Bar dataKey="unique_users" name="Usuários Únicos" fill="#198754" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={4}>
                <Card className="h-100">
                    <Card.Header>📌 Ações por Tipo</Card.Header>
                    <Card.Body>
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={stats?.actionsByType || []}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ action, count }) => `${action}: ${count}`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="count"
                                >
                                    {(stats?.actionsByType || []).map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </Card.Body>
                </Card>
            </Col>
        </Row>

        {/* Top Usuários e Recursos */}
        <Row>
            <Col md={6}>
                <Card>
                    <Card.Header>👤 Usuários Mais Ativos</Card.Header>
                    <Card.Body>
                        <Table hover size="sm">
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(stats?.topUsers || []).slice(0, 5).map((user, i) => (
                                    <tr key={i}>
                                        <td>{user.user_email || 'N/A'}</td>
                                        <td><Badge bg="primary">{user.action_count}</Badge></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={6}>
                <Card>
                    <Card.Header>📂 Recursos Mais Acessados</Card.Header>
                    <Card.Body>
                        <Table hover size="sm">
                            <thead>
                                <tr>
                                    <th>Recurso</th>
                                    <th>Acessos</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(stats?.topResources || []).slice(0, 5).map((res, i) => (
                                    <tr key={i}>
                                        <td>{res.resource}</td>
                                        <td><Badge bg="info">{res.count}</Badge></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    </>
);

const SecurityTab = ({ loginAttempts, alerts, permissionChanges, onMarkAlertRead, formatDate, getSeverityBadge }) => (
    <>
        {/* Alertas */}
        <Row className="mb-4">
            <Col>
                <Card border={alerts.length > 0 ? 'warning' : ''}>
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        🚨 Alertas do Sistema
                        {alerts.length > 0 && <Badge bg="warning">{alerts.length} não lidos</Badge>}
                    </Card.Header>
                    <Card.Body>
                        {alerts.length === 0 ? (
                            <p className="text-muted mb-0">✅ Nenhum alerta ativo</p>
                        ) : (
                            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                {alerts.map(alert => (
                                    <Alert key={alert.id} variant={alert.severity === 'critical' ? 'danger' : alert.severity === 'warning' ? 'warning' : 'info'} dismissible onClose={() => onMarkAlertRead(alert.id)}>
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                <strong>{alert.title}</strong>
                                                <p className="mb-0 small">{alert.message}</p>
                                                <small className="text-muted">{formatDate(alert.created_at)}</small>
                                            </div>
                                            {getSeverityBadge(alert.severity)}
                                        </div>
                                    </Alert>
                                ))}
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Col>
        </Row>

        <Row className="mb-4">
            <Col md={6}>
                <Card>
                    <Card.Header>🔑 Tentativas de Login Recentes</Card.Header>
                    <Card.Body>
                        <Table hover size="sm" responsive>
                            <thead>
                                <tr>
                                    <th>Email</th>
                                    <th>Status</th>
                                    <th>IP</th>
                                    <th>Data</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(loginAttempts.attempts || []).slice(0, 10).map((attempt, i) => (
                                    <tr key={i}>
                                        <td>{attempt.email}</td>
                                        <td>
                                            {attempt.success ? (
                                                <Badge bg="success">✓</Badge>
                                            ) : (
                                                <Badge bg="danger">✗ {attempt.failure_reason}</Badge>
                                            )}
                                        </td>
                                        <td><code>{attempt.ip_address}</code></td>
                                        <td><small>{formatDate(attempt.timestamp)}</small></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={6}>
                <Card>
                    <Card.Header>🔒 Alterações de Permissões</Card.Header>
                    <Card.Body>
                        <Table hover size="sm" responsive>
                            <thead>
                                <tr>
                                    <th>Tipo</th>
                                    <th>Usuário Alvo</th>
                                    <th>Por</th>
                                    <th>Data</th>
                                </tr>
                            </thead>
                            <tbody>
                                {permissionChanges.slice(0, 10).map((change, i) => (
                                    <tr key={i}>
                                        <td><Badge bg="secondary">{change.change_type}</Badge></td>
                                        <td>{change.target_user_email}</td>
                                        <td>{change.changed_by_email}</td>
                                        <td><small>{formatDate(change.timestamp)}</small></td>
                                    </tr>
                                ))}
                                {permissionChanges.length === 0 && (
                                    <tr><td colSpan="4" className="text-muted text-center">Nenhuma alteração registrada</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    </>
);

const SessionsTab = ({ activeSessions, concurrentSessions, usersLastAccess, deviceStats, formatDate, onViewTimeline }) => (
    <>
        {/* Cards */}
        <Row className="mb-4">
            <Col md={3}>
                <Card bg="primary" text="white" className="text-center">
                    <Card.Body>
                        <h6>Sessões Ativas</h6>
                        <h2>{activeSessions.totalActive || 0}</h2>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={3}>
                <Card bg="warning" text="dark" className="text-center">
                    <Card.Body>
                        <h6>Sessões Simultâneas</h6>
                        <h2>{concurrentSessions.length || 0}</h2>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={6}>
                <Card>
                    <Card.Header>📱 Dispositivos (30 dias)</Card.Header>
                    <Card.Body>
                        <ResponsiveContainer width="100%" height={100}>
                            <PieChart>
                                <Pie
                                    data={deviceStats}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={40}
                                    fill="#8884d8"
                                    dataKey="count"
                                    label={({ device_type, count }) => `${device_type}: ${count}`}
                                >
                                    {deviceStats.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </Card.Body>
                </Card>
            </Col>
        </Row>

        <Row>
            <Col md={6}>
                <Card>
                    <Card.Header>👤 Sessões Ativas Agora</Card.Header>
                    <Card.Body>
                        <Table hover size="sm" responsive>
                            <thead>
                                <tr>
                                    <th>Usuário</th>
                                    <th>Dispositivo</th>
                                    <th>Inativo</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(activeSessions.sessions || []).map((session, i) => (
                                    <tr key={i}>
                                        <td>
                                            {session.nome}<br />
                                            <small className="text-muted">{session.email}</small>
                                        </td>
                                        <td>
                                            <Badge bg="secondary">{session.device_type}</Badge><br />
                                            <small>{session.browser}</small>
                                        </td>
                                        <td>{session.inactive_minutes || 0} min</td>
                                        <td>
                                            <Button size="sm" variant="outline-primary" onClick={() => onViewTimeline(session)}>
                                                Timeline
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                {(activeSessions.sessions || []).length === 0 && (
                                    <tr><td colSpan="4" className="text-muted text-center">Nenhuma sessão ativa</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={6}>
                <Card>
                    <Card.Header>📅 Último Acesso por Usuário</Card.Header>
                    <Card.Body>
                        <Table hover size="sm" responsive>
                            <thead>
                                <tr>
                                    <th>Usuário</th>
                                    <th>Último Acesso</th>
                                    <th>Dias Inativo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {usersLastAccess.slice(0, 10).map((user, i) => (
                                    <tr key={i} className={user.days_inactive > 30 ? 'table-warning' : ''}>
                                        <td>
                                            {user.nome}<br />
                                            <small className="text-muted">{user.email}</small>
                                        </td>
                                        <td><small>{formatDate(user.last_session_activity || user.ultimo_acesso)}</small></td>
                                        <td>
                                            <Badge bg={user.days_inactive > 30 ? 'warning' : user.days_inactive > 7 ? 'secondary' : 'success'}>
                                                {user.days_inactive || 0} dias
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    </>
);

const PerformanceTab = ({ slowEndpoints, errorsByType, serverMetrics, formatDuration, formatDate }) => (
    <>
        {/* Métricas do Servidor */}
        {serverMetrics?.current && (
            <Row className="mb-4">
                <Col md={3}>
                    <Card>
                        <Card.Body className="text-center">
                            <h6 className="text-muted">CPU</h6>
                            <h3>{serverMetrics.current.cpu_usage}%</h3>
                            <ProgressBar
                                now={serverMetrics.current.cpu_usage}
                                variant={serverMetrics.current.cpu_usage > 80 ? 'danger' : serverMetrics.current.cpu_usage > 50 ? 'warning' : 'success'}
                            />
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card>
                        <Card.Body className="text-center">
                            <h6 className="text-muted">Memória</h6>
                            <h3>{serverMetrics.current.memory_usage_percent}%</h3>
                            <ProgressBar
                                now={serverMetrics.current.memory_usage_percent}
                                variant={serverMetrics.current.memory_usage_percent > 80 ? 'danger' : serverMetrics.current.memory_usage_percent > 50 ? 'warning' : 'success'}
                            />
                            <small className="text-muted">
                                {serverMetrics.current.memory_used_mb} MB / {serverMetrics.current.memory_total_mb} MB
                            </small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card>
                        <Card.Body className="text-center">
                            <h6 className="text-muted">Uptime</h6>
                            <h3>{Math.floor(serverMetrics.current.uptime_seconds / 3600)}h</h3>
                            <small className="text-muted">{Math.floor(serverMetrics.current.uptime_seconds / 86400)} dias</small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card>
                        <Card.Body className="text-center">
                            <h6 className="text-muted">Node.js</h6>
                            <h3>{serverMetrics.current.node_version}</h3>
                            <small className="text-muted">{serverMetrics.current.platform}</small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        )}

        <Row>
            <Col md={6}>
                <Card>
                    <Card.Header>🐢 Endpoints Lentos ({'>'}1s)</Card.Header>
                    <Card.Body>
                        <Table hover size="sm" responsive>
                            <thead>
                                <tr>
                                    <th>Endpoint</th>
                                    <th>Média</th>
                                    <th>Máx</th>
                                    <th>Chamadas</th>
                                </tr>
                            </thead>
                            <tbody>
                                {slowEndpoints.map((ep, i) => (
                                    <tr key={i}>
                                        <td>
                                            <Badge bg="secondary" className="me-1">{ep.method}</Badge>
                                            <code>{ep.endpoint}</code>
                                        </td>
                                        <td className="text-warning">{formatDuration(Math.round(ep.avg_duration))}</td>
                                        <td className="text-danger">{formatDuration(ep.max_duration)}</td>
                                        <td>{ep.call_count}</td>
                                    </tr>
                                ))}
                                {slowEndpoints.length === 0 && (
                                    <tr><td colSpan="4" className="text-muted text-center">✅ Nenhum endpoint lento</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={6}>
                <Card>
                    <Card.Header>❌ Erros por Tipo (7 dias)</Card.Header>
                    <Card.Body>
                        <Table hover size="sm" responsive>
                            <thead>
                                <tr>
                                    <th>Endpoint</th>
                                    <th>Erro</th>
                                    <th>Qtd</th>
                                </tr>
                            </thead>
                            <tbody>
                                {errorsByType.map((err, i) => (
                                    <tr key={i}>
                                        <td><code>{err.endpoint}</code></td>
                                        <td><small className="text-danger">{err.error_message || 'N/A'}</small></td>
                                        <td><Badge bg="danger">{err.count}</Badge></td>
                                    </tr>
                                ))}
                                {errorsByType.length === 0 && (
                                    <tr><td colSpan="3" className="text-muted text-center">✅ Nenhum erro registrado</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    </>
);

const AnalyticsTab = ({ topProducts, topFeatures, peakHours, usageTrend }) => (
    <>
        {/* Gráfico de Horários de Pico */}
        <Row className="mb-4">
            <Col>
                <Card>
                    <Card.Header>🕐 Horários de Pico (7 dias)</Card.Header>
                    <Card.Body>
                        <ResponsiveContainer width="100%" height={200}>
                            <AreaChart data={peakHours}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" />
                                <YAxis />
                                <Tooltip />
                                <Area type="monotone" dataKey="count" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Card.Body>
                </Card>
            </Col>
        </Row>

        {/* Gráfico de Tendência */}
        <Row className="mb-4">
            <Col>
                <Card>
                    <Card.Header>📈 Tendência de Uso (14 dias)</Card.Header>
                    <Card.Body>
                        <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={usageTrend}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" />
                                <YAxis />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="total_actions" name="Ações" stroke="#0d6efd" />
                                <Line type="monotone" dataKey="unique_users" name="Usuários" stroke="#198754" />
                                <Line type="monotone" dataKey="errors" name="Erros" stroke="#dc3545" />
                            </LineChart>
                        </ResponsiveContainer>
                    </Card.Body>
                </Card>
            </Col>
        </Row>

        <Row>
            <Col md={6}>
                <Card>
                    <Card.Header>📦 Produtos Mais Visualizados (30 dias)</Card.Header>
                    <Card.Body>
                        <Table hover size="sm" responsive>
                            <thead>
                                <tr>
                                    <th>Produto</th>
                                    <th>Views</th>
                                    <th>Usuários</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topProducts.map((prod, i) => (
                                    <tr key={i}>
                                        <td>
                                            <strong>{prod.produto_codigo}</strong><br />
                                            <small className="text-muted">{prod.produto_nome}</small>
                                        </td>
                                        <td><Badge bg="primary">{prod.view_count}</Badge></td>
                                        <td>{prod.unique_viewers}</td>
                                    </tr>
                                ))}
                                {topProducts.length === 0 && (
                                    <tr><td colSpan="3" className="text-muted text-center">Nenhum dado disponível</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </Col>
            <Col md={6}>
                <Card>
                    <Card.Header>⭐ Funcionalidades Mais Usadas (30 dias)</Card.Header>
                    <Card.Body>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={topFeatures.slice(0, 10)} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" />
                                <YAxis dataKey="feature" type="category" width={100} />
                                <Tooltip />
                                <Bar dataKey="usage_count" fill="#0d6efd" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    </>
);

const AuditTab = ({ logs, filters, setFilters, pagination, stats, loading, onSearch, onExport, onViewDetails, formatDate, getActionBadge, getStatusBadge }) => (
    <>
        {/* Filtros */}
        <Card className="mb-4">
            <Card.Body>
                <Row>
                    <Col md={2}>
                        <Form.Group>
                            <Form.Label>Usuário</Form.Label>
                            <Form.Select
                                value={filters.userId}
                                onChange={(e) => setFilters({ ...filters, userId: e.target.value, offset: 0 })}
                            >
                                <option value="">Todos</option>
                                {(stats?.users || []).map(u => (
                                    <option key={u.usuario_id} value={u.usuario_id}>{u.user_email}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={2}>
                        <Form.Group>
                            <Form.Label>Tipo de Ação</Form.Label>
                            <Form.Select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value, offset: 0 })}>
                                <option value="">Todas</option>
                                <option value="LOGIN">Login</option>
                                <option value="LOGOUT">Logout</option>
                                <option value="CREATE">Criar</option>
                                <option value="UPDATE">Atualizar</option>
                                <option value="DELETE">Deletar</option>
                                <option value="READ">Consultar</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={2}>
                        <Form.Group>
                            <Form.Label>Status</Form.Label>
                            <Form.Select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value, offset: 0 })}>
                                <option value="">Todos</option>
                                <option value="success">Sucesso</option>
                                <option value="failure">Erro</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col md={2}>
                        <Form.Group>
                            <Form.Label>Data Início</Form.Label>
                            <DatePicker
                                value={filters.startDate}
                                onChange={(valor) => setFilters({ ...filters, startDate: valor })}
                                placeholder="Data início"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2}>
                        <Form.Group>
                            <Form.Label>Data Fim</Form.Label>
                            <DatePicker
                                value={filters.endDate}
                                onChange={(valor) => setFilters({ ...filters, endDate: valor })}
                                placeholder="Data fim"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="d-flex align-items-end gap-1">
                        <Button variant="primary" onClick={onSearch}>Buscar</Button>
                        <Button variant="success" size="sm" onClick={() => onExport('csv')}>CSV</Button>
                    </Col>
                </Row>
                <Row className="mt-2">
                    <Col md={6}>
                        <Form.Control
                            type="text"
                            placeholder="Buscar por email, endpoint, recurso..."
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        />
                    </Col>
                </Row>
            </Card.Body>
        </Card>

        {/* Tabela de Logs */}
        <Card>
            <Card.Body>
                {loading ? (
                    <div className="text-center py-5"><Spinner animation="border" /></div>
                ) : (
                    <>
                        <Table hover responsive>
                            <thead>
                                <tr>
                                    <th>Data/Hora</th>
                                    <th>Usuário</th>
                                    <th>Ação</th>
                                    <th>Recurso</th>
                                    <th>Status</th>
                                    <th>Duração</th>
                                    <th>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((log) => (
                                    <tr key={log.id}>
                                        <td>{formatDate(log.timestamp)}</td>
                                        <td>{log.user_email || 'N/A'}</td>
                                        <td>{getActionBadge(log.acao || log.action)}</td>
                                        <td>
                                            {log.recurso || log.resource}
                                            {log.resource_id && <><br /><small className="text-muted">{log.resource_id}</small></>}
                                        </td>
                                        <td>{getStatusBadge(log.status)}</td>
                                        <td>{log.duration_ms}ms</td>
                                        <td>
                                            <Button size="sm" variant="outline-primary" onClick={() => onViewDetails(log)}>
                                                Ver
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        {/* Paginação */}
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div>
                                Mostrando {filters.offset + 1} - {Math.min(filters.offset + filters.limit, pagination.total)} de {pagination.total}
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-primary"
                                    disabled={filters.offset === 0}
                                    onClick={() => { setFilters({ ...filters, offset: Math.max(0, filters.offset - filters.limit) }); }}
                                >
                                    Anterior
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    disabled={!pagination.hasMore}
                                    onClick={() => { setFilters({ ...filters, offset: filters.offset + filters.limit }); }}
                                >
                                    Próxima
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </Card.Body>
        </Card>
    </>
);

export default LogsViewer;

